import { Button, Kind, Size } from '@thanx/uikit/button'
import {
  getBaseStyle,
  getColorStyle,
  getSizeStyle,
} from '@thanx/uikit/button/style'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { Theme } from '@thanx/uikit/theme'
import type {
  LiveUpdatesConfiguration,
  LiveUpdatesStack,
} from 'api/liveUpdates.types'
import ArrowRight from 'components/Icons/ArrowRight'
import ChefHat from 'components/Icons/ChefHat'
import Clock from 'components/Icons/Clock'
import DeliveryTruck from 'components/Icons/DeliveryTruck'
import OrderCanceled from 'components/Icons/OrderCanceled'
import OrderConfirmed from 'components/Icons/OrderConfirmed'
import ShoppingBag from 'components/Icons/ShoppingBag'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import ConfigurationPicker from '../ConfigurationPicker'
import DetailStatusPlaceholder from '../DetailStatusPlaceholder'
import LockedCard, { UNSUPPORTED_GROUP_KEYS } from '../LockedCard'
import type { TrackerStage } from '../preview'
import { stackChips } from '../stackChips'
import ConnectionActionIcon from './ConnectionActionIcon'
import {
  deliveryJourney,
  needsSetupAction,
  TONE_COLORS,
} from './providerStatus'
import type { ProviderStatusContent } from './providerStatus'

const t = buildTranslate('live_updates.overview.status')
const tLocked = buildTranslate('live_updates.locked')
const tErrors = buildTranslate('live_updates.errors')

// The rail previews the guest's JOURNEY in stages, not the nine wire statuses
// (Sketch prototype v35): picked_up / en_route / arrived / delivered are one
// "Delivery" stage, the same collapse the guest's own in-app tracker makes.
// Showing each status here would duplicate the Configuration tab.
type RailStage = 'placed' | 'preparing' | 'ready' | 'delivery' | 'canceled'

// Which tracker stages feed each rail stage — a rail stage is off only once
// every stage behind it is.
const RAIL_STAGE_SOURCES: Record<RailStage, TrackerStage[]> = {
  placed: ['placed'],
  preparing: ['preparing'],
  ready: ['ready'],
  delivery: ['on_the_way', 'delivered'],
  canceled: ['canceled'],
}

const STAGE_ICONS: Record<
  RailStage,
  (props: { strokeColor?: string }) => React.ReactElement
> = {
  placed: OrderConfirmed,
  preparing: ChefHat,
  ready: ShoppingBag,
  delivery: DeliveryTruck,
  canceled: OrderCanceled,
}

// A guest is on exactly one of the pickup and delivery journeys, never both:
// "Ready" fires only for pickup orders and the delivery stages only for
// delivery orders, so a single combined ladder would show a sequence nobody
// ever sees. Both are previewed, one rail each, so a merchant can see what
// either kind of guest gets without having to go looking for the other.
//
// Canceled is a track of its own rather than a stage on either ladder: both
// canceled statuses map to a tracker stage no journey contains (see
// preview/types.ts), and either kind of order can end there. So it is a single
// unconnected stage (Sketch prototype v40 `canceledCol`).
export type Journey = 'pickup' | 'delivery' | 'canceled'

// The qualifiers a stage label can carry. A named union rather than a bare
// string so a typo can't reach t() as a silently-missing locale key.
type StageSublabel =
  | 'estimated'
  | 'not_available'
  | 'unlock_with_olo'
  | 'waiting_for_data'
  | 'may_not_be_sending'

// The Olo connection's qualifiers, shown under each stage they affect (Sketch
// prototype: the banner alone "tested worse — too disconnected"). They sit
// further below the label than "Estimated" and read as captions, in the
// prototype's `.glabel2` / `.wait-lbl2` / `.olo-btn2` treatment.
const OLO_CAPTION_GAP = 16

// The prototype's amber (#A27E11) is ~3.8:1 on white, under AA for this
// small bold text; this is the same hue darkened to pass (4.57:1). The
// palette's only gold, eggYolk, is a fill tone that falls further short.
const AMBER_TEXT_COLOR = '#92710F'

type RailStageView = {
  key: RailStage
  state: RailStageState
  sublabelKey: StageSublabel | undefined
}

const JOURNEYS: Record<Journey, RailStage[]> = {
  pickup: ['placed', 'preparing', 'ready'],
  delivery: ['placed', 'preparing', 'delivery'],
  canceled: ['canceled'],
}

// Every stage column is exactly this wide, so an icon always sits half of it
// from each edge and the gap either side of a divider stays the same however
// long the label beside it is (Sketch prototype v40 `--tr-node`).
const STAGE_COLUMN_WIDTH = 116
// The space either side of the divider between two tracks. The side leading
// into the next track is wider, because that track opens with a heading and a
// long label that would otherwise crowd the line (v40 `--tr-gutter-out` /
// `--tr-gutter-in`).
const DIVIDER_GUTTER_BEFORE = 22
const DIVIDER_GUTTER_AFTER = 48
const DIVIDER_WIDTH = 1
// Everything a track carries ahead of its stages: both gutters and the rule.
const TRACK_LEAD = DIVIDER_GUTTER_BEFORE + DIVIDER_WIDTH + DIVIDER_GUTTER_AFTER
// Between lines of tracks, once they wrap.
const TRACK_ROW_GAP = 28
// How far a connecting segment stops short of each stage's icon.
const SEGMENT_INSET = 16

// A pending stage sits in a rounded tile rather than bare, so its segment stops
// further out to clear it (Sketch prototype v35: 16px vs 22px).
const PENDING_SEGMENT_INSET = 22

// uikit's secondary button with a grey rather than blue border (Sketch
// prototype v41 `.cfggo`). uikit spreads a consumer BaseButton override over
// its own styling, replacing it, so its secondary style is rebuilt here from
// uikit's own style functions and only the border colour changed — keeping
// its hover/active fills and baseui's focus ring.
const GREY_BORDER_BUTTON_OVERRIDES = {
  BaseButton: {
    style: (styleArgs: { $theme: Theme }) => ({
      ...getBaseStyle(styleArgs),
      ...getColorStyle(Kind.SECONDARY, styleArgs, false),
      ...getSizeStyle(Size.STANDARD),
      borderColor: palette.grey30,
    }),
  },
}

// A merchant-disabled stage is HIDDEN rather than greyed — it is off because
// the merchant switched it off, and this page is aimed at merchants new to the
// feature, who should see the journey they actually ship. (Superseded the
// earlier greyed treatment; Sketch prototype v35 `railHTML2` filters it out.)
// The states below are therefore only the ones that still render.
type RailStageState = 'available' | 'activating' | 'unlock' | 'stale' | 'na'

// Per-state node treatment, traced from the prototype's `.nd-*` rules. One
// colour has no palette equivalent and is noted where it deviates.
const STAGE_VISUALS: Record<
  RailStageState,
  {
    icon: string
    iconOpacity?: number
    label: 'grey90' | 'grey50'
    sublabel: 'grey70' | 'grey50'
    tile?: { background: string; border: string }
    segment: { color: string; dashed: boolean; opacity: number; inset: number }
  }
> = {
  available: {
    icon: palette.nephritis,
    label: 'grey90',
    sublabel: 'grey70',
    segment: {
      color: palette.nephritis,
      dashed: false,
      opacity: 0.55,
      inset: SEGMENT_INSET,
    },
  },
  activating: {
    icon: palette.belizeHole,
    label: 'grey90',
    sublabel: 'grey70',
    // The prototype borders this tile in a mid blue (#cfe6f7) the palette has
    // no token between belizeHole10 and belizeHole for, so the tint carries
    // the shape on its own.
    tile: { background: palette.belizeHole10, border: palette.belizeHole10 },
    segment: {
      color: palette.belizeHole,
      dashed: true,
      opacity: 0.55,
      inset: PENDING_SEGMENT_INSET,
    },
  },
  unlock: {
    icon: palette.belizeHole,
    label: 'grey90',
    sublabel: 'grey70',
    tile: { background: palette.belizeHole10, border: palette.belizeHole10 },
    segment: {
      color: palette.belizeHole,
      dashed: true,
      opacity: 0.55,
      inset: PENDING_SEGMENT_INSET,
    },
  },
  stale: {
    icon: palette.grey40,
    label: 'grey50',
    sublabel: 'grey50',
    segment: {
      color: palette.grey40,
      dashed: true,
      opacity: 0.6,
      inset: SEGMENT_INSET,
    },
  },
  na: {
    icon: palette.grey40,
    iconOpacity: 0.75,
    label: 'grey50',
    sublabel: 'grey50',
    segment: {
      color: palette.grey30,
      dashed: true,
      opacity: 0.7,
      inset: SEGMENT_INSET,
    },
  },
}

// One stage column: its leading connector, the glyph (tiled when the stage
// isn't simply live), the label and an optional sublabel qualifying it
// ("Estimated", "Not available") or an Olo connection caption.
const StageNode = ({
  stageKey,
  state,
  sublabelKey,
  showSegment,
  onUnlock,
}: {
  stageKey: RailStage
  state: RailStageState
  sublabelKey: StageSublabel | undefined
  showSegment: boolean
  onUnlock: () => void
}) => {
  const [css] = useStyletron()
  const visuals = STAGE_VISUALS[state]
  const Icon = STAGE_ICONS[stageKey]

  return (
    <div
      className={css({
        position: 'relative',
        flex: 'none',
        width: `${STAGE_COLUMN_WIDTH}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      })}
    >
      {/* Drawn inside the column it leads into and pulled back over the
          previous one, so connectors never take part in the row's own
          sizing. The solid case is a filled bar rather than a border — a
          2.5px border rounds down to 2 and reads thinner than the dashes
          beside it. */}
      {showSegment && (
        <span
          aria-hidden="true"
          data-testid="lu-overview-stage-segment"
          className={css({
            position: 'absolute',
            top: '19px',
            left: `calc(-50% + ${visuals.segment.inset}px)`,
            width: `calc(100% - ${visuals.segment.inset * 2}px)`,
            opacity: visuals.segment.opacity,
            ...(visuals.segment.dashed
              ? { borderTop: `2.5px dashed ${visuals.segment.color}` }
              : { height: '2.5px', backgroundColor: visuals.segment.color }),
          })}
        />
      )}
      <span
        aria-hidden="true"
        className={css({
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          ...(visuals.iconOpacity === undefined
            ? {}
            : { opacity: visuals.iconOpacity }),
          ...(visuals.tile
            ? {
                backgroundColor: visuals.tile.background,
                border: `1px solid ${visuals.tile.border}`,
                borderRadius: '10px',
              }
            : {}),
        })}
      >
        <Icon strokeColor={visuals.icon} />
      </span>
      {/* Label and qualifier each stay on one line, overflowing the column
          rather than wrapping: its width is fixed so the rail's spacing never
          depends on its copy. */}
      <Text.BodyText4
        bold
        color={visuals.label}
        className={css({
          textAlign: 'center',
          marginTop: '11px',
          whiteSpace: 'nowrap',
        })}
      >
        {t(`stages.${stageKey}`)}
      </Text.BodyText4>
      {sublabelKey === 'unlock_with_olo' && (
        <div className={css({ marginTop: `${OLO_CAPTION_GAP}px` })}>
          {/* Named for its stage, since Delivery and Canceled each carry one;
              the visible text leads the name so voice control still finds it. */}
          <Button
            kind="minimal"
            size="small"
            onClick={onUnlock}
            aria-label={t('sublabels.unlock_with_olo_aria', {
              stage: t(`stages.${stageKey}`),
            })}
          >
            <span
              className={css({
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              })}
            >
              {t('sublabels.unlock_with_olo')}
              <ArrowRight fillColor={palette.belizeHole} />
            </span>
          </Button>
        </div>
      )}
      {(sublabelKey === 'waiting_for_data' ||
        sublabelKey === 'may_not_be_sending') && (
        <Text.BodyText5
          bold
          className={css({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            marginTop: `${OLO_CAPTION_GAP}px`,
            color: AMBER_TEXT_COLOR,
            fontSize: '13px',
            fontWeight: 900,
            whiteSpace: 'nowrap',
          })}
        >
          {sublabelKey === 'waiting_for_data' && (
            <Clock fillColor={AMBER_TEXT_COLOR} size={14} />
          )}
          {t(`sublabels.${sublabelKey}`)}
        </Text.BodyText5>
      )}
      {(sublabelKey === 'estimated' || sublabelKey === 'not_available') && (
        <Text.BodyText4
          bold
          color={visuals.sublabel}
          className={css({
            marginTop: '3px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          })}
        >
          {t(`sublabels.${sublabelKey}`)}
        </Text.BodyText4>
      )}
    </div>
  )
}

// One track: its heading, then the connected-node rail.
const JourneyRail = ({
  journey,
  stages,
  onUnlock,
}: {
  journey: Journey
  stages: RailStageView[]
  onUnlock: () => void
}) => {
  const [css] = useStyletron()

  // Only as wide as its own stages, so the tracks pack from the left (v40
  // `.rails.side`) instead of each being centred in an equal share of the row.
  return (
    <div
      data-testid={`lu-overview-journey-${journey}`}
      className={css({ flex: 'none' })}
    >
      {/* The locale keeps sentence case so translations aren't shouted;
          text-transform gives the prototype's uppercase eyebrow. */}
      <Text.BodyText5
        tag="h3"
        bold
        color="grey70"
        className={css({
          display: 'block',
          margin: '0 0 16px',
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
        })}
      >
        {t(`journeys.${journey}`)}
      </Text.BodyText5>
      <div
        data-testid={`lu-overview-stage-rail-${journey}`}
        className={css({ display: 'flex', alignItems: 'flex-start' })}
      >
        {/* A track's first stage has nothing to connect back to — which also
            keeps the single-stage Canceled track unconnected. */}
        {stages.map((stage, index) => (
          <StageNode
            key={stage.key}
            stageKey={stage.key}
            state={stage.state}
            sublabelKey={stage.sublabelKey}
            showSegment={index > 0}
            onUnlock={onUnlock}
          />
        ))}
      </div>
    </div>
  )
}

export interface ProviderStatusSectionProps {
  stack: LiveUpdatesStack
  deliveryConfigurable: boolean
  /** False for an unsupported (non-configurable) group — FR-76. The picker
   * (and the detected stack line) stay put either way, so a merchant who
   * lands on an ineligible configuration can still switch back out of it;
   * only the rail/action content below is replaced by the locked notice. */
  isSupported: boolean
  /** Resolved once by OverviewTab so the banner above the hero and this
   * section can never disagree about the connection's state. Undefined only
   * while an Olo connection's queries haven't settled yet — for every other
   * kind, including an unsupported group (never Olo per stackPresets.ts),
   * `resolveProviderStatusContent` always returns a defined object. The
   * `!isSupported` branch below ignores `content` either way, so a real,
   * defined value reaching it for an unsupported group is harmless. */
  content: ProviderStatusContent | undefined
  onManageOloWebhook: () => void
  /** Tracker stages whose statuses are ALL switched off for this
   * configuration — guests never see them, so the preview must not show
   * them. Same set the Configuration tab's live preview is driven by. */
  disabledStages: Set<TrackerStage>
  /** Tracker stages this configuration fires from the estimated ready time
   * rather than a provider event — the rail marks them "Estimated" so the
   * merchant can see which stages don't depend on the integration at all. */
  timerStages: Set<TrackerStage>
  configurations: LiveUpdatesConfiguration[]
  selectedId: string | null
  onSelectConfiguration: (selected: { value: string }[]) => void
  /** Leaves the read-only preview for the Configuration tab, where the
   * merchant can actually change this configuration. */
  onConfigure: () => void
  /** True while the SELECTED configuration's own detail (statuses) hasn't
   * caught up to a switch yet — `disabledStages`/`timerStages` default to
   * empty during this gap, which would otherwise read as "nothing is
   * disabled" rather than "not known yet". Swaps the rail for a scoped
   * loading placeholder instead of rendering it from that misleading empty
   * state. */
  isDetailLoading: boolean
  /** True once the SELECTED configuration's detail will never become
   * previewable as fetched — either a genuine fetch error, or a detail that
   * loaded but can't report a numeric version. Mutually exclusive with
   * `isDetailLoading`. Replaces the rail with a retry notice in the same
   * slot, rather than a spinner that would never resolve. */
  isDetailUnavailable: boolean
  /** Retries the selected configuration's detail fetch — wired to the retry
   * notice shown while `isDetailUnavailable` is true. */
  onRetryDetail: () => void
}

// FR-36/FR-37: the real (not marketing-fixed) connection status for this
// configuration — heading/action derived from the actual Olo webhook state
// (or a flat "automatic" for every other provider, which has no webhook
// dependency at all), a stage rail showing which order-lifecycle stages are
// live vs. still gated behind Olo, and the provider stack itself.
const ProviderStatusSection = ({
  stack,
  deliveryConfigurable,
  isSupported,
  content,
  onManageOloWebhook,
  disabledStages,
  timerStages,
  configurations,
  selectedId,
  onSelectConfiguration,
  onConfigure,
  isDetailLoading,
  isDetailUnavailable,
  onRetryDetail,
}: ProviderStatusSectionProps) => {
  const [css] = useStyletron()

  const needsSetup = isSupported && !!content && needsSetupAction(content)

  const toneColors = content ? TONE_COLORS[content.action.tone] : undefined

  const delivery = deliveryJourney(stack, deliveryConfigurable)
  // Pickup, delivery, then canceled — the same order the Configuration tab
  // lists its sections in (`sections.ts` SECTION_BLUEPRINT). A configuration
  // with no delivery integration at all has no delivery journey to preview.
  const journeys: Journey[] =
    delivery === 'none'
      ? ['pickup', 'canceled']
      : ['pickup', 'delivery', 'canceled']

  // A rail stage is off only once every tracker stage feeding it is off.
  //
  // Deliberately unlike `preview/track.ts:deriveTrack`, which keeps a journey's
  // first and last stage even when disabled: that is a guest-facing progress
  // bar, and it needs both endpoints to read as one. This rail is a merchant's
  // summary of the journey they actually ship, so a stage they switched off has
  // no business appearing in it — including a trailing one such as Ready, which
  // (unlike the required `placed`) really can be switched off. The two surfaces
  // therefore differ on purpose for the same configuration.
  const isDisabled = (stage: RailStage): boolean =>
    RAIL_STAGE_SOURCES[stage].every(source => disabledStages.has(source))

  // Only delivery and cancellation are ever anything but plainly live: the
  // kitchen ladder reaches Thanx through the ordering provider, while for Olo
  // those two arrive only as webhooks, which may not be connected, may not
  // have fired yet, or may have gone quiet. Delivery also depends on a
  // separate integration that may report nothing at all.
  const stageState = (
    stage: RailStage,
    content: ProviderStatusContent
  ): RailStageState => {
    if (stage !== 'delivery' && stage !== 'canceled') return 'available'
    if (stage === 'delivery' && delivery === 'not_available') return 'na'
    if (content.oloStagesGated) {
      return content.waitingForFirstEvent ? 'activating' : 'unlock'
    }
    if (content.connectionStatus === 'reconnect') return 'stale'
    return 'available'
  }

  const sublabelKey = (
    stage: RailStage,
    state: RailStageState
  ): StageSublabel | undefined => {
    if (state === 'unlock') return 'unlock_with_olo'
    if (state === 'activating') return 'waiting_for_data'
    if (state === 'stale') return 'may_not_be_sending'
    if (state === 'na') return 'not_available'
    const isEstimated = RAIL_STAGE_SOURCES[stage].some(source =>
      timerStages.has(source)
    )
    return state === 'available' && isEstimated ? 'estimated' : undefined
  }

  // A track left with nothing to show is dropped rather than rendered as an
  // unexplained blank band under its heading — Canceled whenever both canceled
  // statuses are switched off; the other two never in practice, since `placed`
  // is required. There is no rail at all for an unsupported group — its
  // content is undefined, and the section renders the locked notice instead.
  const tracks = content
    ? journeys
        .map(journey => ({
          journey,
          stages: JOURNEYS[journey]
            .filter(stage => !isDisabled(stage))
            .map(stage => {
              const state = stageState(stage, content)
              return {
                key: stage,
                state,
                sublabelKey: sublabelKey(stage, state),
              }
            }),
        }))
        .filter(track => track.stages.length > 0)
    : []

  const chips = stackChips(stack)
  // Only the 'generic' kind's copy carries a %{provider} placeholder — passing
  // it to every t() call here is harmless for the others (unused interpolation
  // args are ignored).
  const interpolation = { provider: stack.orderingProvider?.displayName ?? '' }

  return (
    <div data-testid="lu-overview-status">
      {/* A fixed heading for the whole block. The live connection state is
          reported by the action banner at the foot of the card (and by the
          page banner above the hero) — not by re-titling the section. */}
      <Text.Header3
        tag="h2"
        className={css({ margin: '0 0 18px', paddingTop: '8px' })}
      >
        {t('preview_title')}
      </Text.Header3>

      {/* Picker, rail and the action banner share one card (Sketch prototype
          v34 `.setup`: "one section, one container — the config picker and
          preview live in the same card") rather than sitting loose on the
          page background. */}
      <div
        className={css({
          padding: '24px 26px',
          borderRadius: '12px',
          backgroundColor: palette.white,
          border: `1px solid ${palette.grey30}`,
        })}
      >
        <ConfigurationPicker
          configurations={configurations}
          selectedId={selectedId}
          onSelectConfiguration={onSelectConfiguration}
          labelKey="preview_label"
          ariaLabelKey="preview_aria_label"
          fixedWidth
        >
          {/* Only once nothing is outstanding: while setup is still pending
              the action banner below already says what to do, and offering a
              second way out of the page alongside it competes with it. Never
              for an unsupported group either — there's nothing to configure,
              so a "Configure setup" link would dead-end on the FR-76
              billboard below. */}
          {isSupported && !needsSetup && (
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap',
              })}
            >
              <Text.BodyText4 color="grey70">
                {t('read_only_note')}
              </Text.BodyText4>
              <Button
                kind="secondary"
                onClick={onConfigure}
                overrides={GREY_BORDER_BUTTON_OVERRIDES}
              >
                <span
                  className={css({
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  })}
                >
                  {t('configure_cta')}
                  <ArrowRight fillColor={palette.belizeHole} />
                </span>
              </Button>
            </div>
          )}
        </ConfigurationPicker>

        {/* The detected stack read back in one line, directly under the
            control that chose it (v34 `cfgLine`) — the same pairs, from the
            same helper, as the Configuration tab's chip line. */}
        {chips.length > 0 && (
          <div
            data-testid="lu-overview-stack-summary"
            className={css({ marginTop: '12px' })}
          >
            {chips.map((chip, index) => (
              <React.Fragment key={chip.label}>
                <Text.BodyText4 color="grey70">
                  {`${index > 0 ? ` ${t('summary.separator')} ` : ''}${
                    chip.label
                  } `}
                </Text.BodyText4>
                <Text.BodyText4 bold color="grey90">
                  {chip.name}
                </Text.BodyText4>
              </React.Fragment>
            ))}
          </div>
        )}

        <div
          className={css({
            height: '1px',
            backgroundColor: palette.grey20,
            margin: '24px 0',
          })}
        />

        {isSupported ? (
          <>
            {isDetailUnavailable ? (
              // The detail loaded but can't report a numeric version — it
              // will never resolve into a real rail, so a spinner here would
              // spin forever. A retry notice in the same slot instead.
              <DetailStatusPlaceholder
                state="unavailable"
                data-testid="lu-overview-tracks-unavailable"
                retryAriaLabel={tErrors('retry_setup_preview_aria')}
                onRetry={onRetryDetail}
              />
            ) : isDetailLoading ? (
              // The switch hasn't caught up yet — `disabledStages`/
              // `timerStages` are transiently empty for THIS configuration,
              // which would otherwise render the rail as if nothing were
              // disabled. A scoped placeholder here (rather than the real
              // rail) avoids that flash; it resolves on its own once the
              // new detail arrives.
              <DetailStatusPlaceholder
                state="loading"
                data-testid="lu-overview-tracks-loading"
                retryAriaLabel={tErrors('retry_setup_preview_aria')}
                onRetry={onRetryDetail}
                loadingPadding="32px 0"
              />
            ) : (
              /* One track per journey, packed from the left with a rule between
                 each. A guest is on one journey or the other, so both are shown
                 rather than folded into a ladder nobody sees — and shown together,
                 so they read as a comparison.

                 Tracks wrap when the card is too narrow. Every track carries the
                 gutters and rule on its leading side, and the row is pulled left by
                 exactly that much inside a clipping box, so whichever track starts
                 a line has its rule clipped away and its stages flush left. Scrolls
                 sideways only if a single track is wider than the card. */
              <div
                data-testid="lu-overview-tracks"
                className={css({ overflowX: 'auto' })}
              >
                <div
                  className={css({
                    display: 'flex',
                    flexWrap: 'wrap',
                    rowGap: `${TRACK_ROW_GAP}px`,
                    marginLeft: `-${TRACK_LEAD}px`,
                  })}
                >
                  {tracks.map((track, index) => (
                    <div
                      key={track.journey}
                      className={css({
                        position: 'relative',
                        flex: 'none',
                        marginLeft: `${DIVIDER_GUTTER_BEFORE}px`,
                        paddingLeft: `${
                          DIVIDER_WIDTH + DIVIDER_GUTTER_AFTER
                        }px`,
                      })}
                    >
                      {index > 0 && (
                        <span
                          aria-hidden="true"
                          data-testid="lu-overview-track-divider"
                          className={css({
                            position: 'absolute',
                            left: 0,
                            top: '2px',
                            bottom: '2px',
                            width: `${DIVIDER_WIDTH}px`,
                            backgroundColor: palette.grey20,
                          })}
                        />
                      )}
                      <JourneyRail
                        journey={track.journey}
                        stages={track.stages}
                        onUnlock={onManageOloWebhook}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Everything the merchant still has to act on. It renders only
                while something is outstanding (Sketch prototype v34 gates
                `#stackblock` on the action carrying a CTA) — once the
                connection is healthy the card is purely the read-only
                preview above. */}
            {needsSetup && content && toneColors && (
              <div
                data-testid="lu-overview-setup-action"
                className={css({
                  marginTop: '30px',
                  paddingTop: '22px',
                  borderTop: `1px solid ${palette.grey20}`,
                })}
              >
                {/* The one place in this card that asks for something, so it
                    sits at the end of what it's talking about. */}
                <div
                  role="status"
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    flexWrap: 'wrap',
                    padding: '20px 22px',
                    borderRadius: '8px',
                    border: `1px solid ${toneColors.border}`,
                    backgroundColor: toneColors.background,
                  })}
                >
                  <span
                    aria-hidden="true"
                    className={css({ display: 'flex', flexShrink: 0 })}
                  >
                    <ConnectionActionIcon status={content.connectionStatus} />
                  </span>
                  <div className={css({ flex: '1 1 0%', minWidth: 0 })}>
                    <Text.BodyText4
                      tag="h4"
                      bold
                      color="grey90"
                      className={css({ margin: 0 })}
                    >
                      {t(content.action.titleKey, interpolation)}
                    </Text.BodyText4>
                    <Text.BodyText4
                      color="grey70"
                      className={css({ display: 'block' })}
                    >
                      {t(content.action.bodyKey, {
                        ...interpolation,
                        ...content.action.bodyInterpolation,
                      })}
                    </Text.BodyText4>
                  </div>
                  {content.action.ctaKey && (
                    <Button kind="minimal" onClick={onManageOloWebhook}>
                      <span
                        className={css({
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        })}
                      >
                        {t(content.action.ctaKey)}
                        <ArrowRight fillColor={palette.belizeHole} />
                      </span>
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          // FR-76: an ineligible configuration keeps the picker (above) and
          // the detected stack (Sandbox QA — merchants with a mix of
          // configured and unsupported location groups need a way back to a
          // working one), but has no rail or connection state to show —
          // the same locked billboard the Configuration tab renders instead
          // of its status sections, so the two tabs never disagree about
          // what "unsupported" looks like.
          <LockedCard
            title={tLocked('unsupported_title')}
            body={tLocked('unsupported_body')}
            rows={UNSUPPORTED_GROUP_KEYS.map(key => ({
              key,
              label: tLocked(`unsupported_groups.${key}`),
            }))}
          />
        )}
      </div>
    </div>
  )
}

export default ProviderStatusSection
